"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { FC, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { FolderIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import toast from "react-hot-toast";
import SelectVideoPublicationForm from "../../../../components/SelectVideoPublicationForm";
import Calendar from "../../../../components/Calendar";
import CalendarAutoPublish from "../../../../components/autoPublish/CalendarAutoPublish";
import CalendarPostPopover from "../../../../components/calendar/CalendarPostPopover";
import { filterProjectPublications, getUnfilteredCalendarHref } from "../../../../components/calendar/projectCalendar";
import Button from "../../../../components/Button";
import Spinner from "../../../../components/Spinner";
import { useFolder } from "../../../../hooks/useFolder";
import { useResourceName } from "../../../../hooks/useResource";
import { editorHrefForVideo } from "../../../../components/publicationFlow/route/editorUrl";
import { scheduleHrefForPublication } from "../../../../components/publishFlow";
import { useCalendarPostPopover } from "../../../../hooks/useCalendarPostPopover";
import { useReadPublications } from "../../../../hooks/usePublications";
import { useDeletePublicationAction } from "../../../../hooks/useDeletePublicationAction";
import { useNow } from "../../../../hooks/useNow";
import { usePostNowAction } from "../../../../hooks/usePostNowAction";
import ConfirmAction from "../../../../components/ConfirmAction";
import TrustpilotReviewDialog, {
  TRUSTPILOT_REVIEW_PROMPT_DISMISS_COOLDOWN_MS,
  TRUSTPILOT_REVIEW_PROMPT_QUERY_PARAM,
  TRUSTPILOT_REVIEW_PROMPT_STORAGE_KEY,
} from "../../../../components/TrustpilotReviewDialog";
import { ModalContext } from "../../../../hooks/useModal";
import { isDemoPublication } from "../../../../utils/demoMode";
import {
  useEmptyStateStatus,
  useOpenScheduleModal,
} from "../../../../hooks/useEmptyState";

type TrustpilotReviewPromptStorage = {
  status: "completed" | "dismissed";
  updatedAt: number;
};

const readTrustpilotReviewPromptStorage = () => {
  try {
    const stored = localStorage.getItem(TRUSTPILOT_REVIEW_PROMPT_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as TrustpilotReviewPromptStorage;
  } catch {
    return null;
  }
};

const shouldShowTrustpilotReviewPrompt = () => {
  const stored = readTrustpilotReviewPromptStorage();
  if (!stored) return true;
  if (stored.status === "completed") return false;
  return (
    Date.now() - stored.updatedAt >
    TRUSTPILOT_REVIEW_PROMPT_DISMISS_COOLDOWN_MS
  );
};

const storeTrustpilotReviewPromptState = (
  status: TrustpilotReviewPromptStorage["status"],
) => {
  localStorage.setItem(
    TRUSTPILOT_REVIEW_PROMPT_STORAGE_KEY,
    JSON.stringify({ status, updatedAt: Date.now() }),
  );
};

// Demo posts exist only in this browser: nothing on the server to edit,
// post or delete.
const showDemoPostNotice = () =>
  toast("This is a demo post. Turn off demo mode in Account to manage real posts.");

/** Mount the name query only when a project filter is present. */
const ProjectCalendarFilter: FC<{ projectId: string; onClear: () => void }> = ({ projectId, onClear }) => {
  const { data: name } = useResourceName(projectId);
  return (
    <div className="flex min-w-0 items-center gap-2 pt-4">
      <FolderIcon className="size-4 shrink-0 text-slate-500" />
      <span className="min-w-0 truncate text-sm" title={name || "Project"}>{name || "Project"}</span>
      <Button type="button" size="s" variant="tertiary" className="shrink-0" onClick={onClear}>Show all</Button>
    </div>
  );
};

const CalendarContent: FC = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const modal = useContext(ModalContext);
  const handledTrustpilotPromptRef = useRef(false);
  const status = useEmptyStateStatus();
  const openScheduleModal = useOpenScheduleModal();
  // The post popover: a click (or a `#<publicationId>` deep link) opens
  // it on the tile; its actions run through the shared hooks whose
  // dialogs stack at page level, with the popover closed first.
  const popover = useCalendarPostPopover();
  // Share the grid's cache for live post details. Calendar reports the
  // displayed, selectable subset separately for previous/next navigation.
  const publicationsQuery = useReadPublications();
  const publications = publicationsQuery.data;
  const projectId = searchParams.get("project")?.trim() || undefined;
  const folder = useFolder(projectId);
  const projectError = projectId ? folder.error || publicationsQuery.error : null;
  const projectLoading = Boolean(projectId && !projectError && (folder.isLoading || publicationsQuery.isLoading || publications === undefined));
  const displayedPublications = useMemo(() => {
    if (!projectId) return publications;
    if (projectLoading || projectError) return [];
    const videoIds = new Set([...folder.videos, ...folder.drafts].map(video => video.id));
    return filterProjectPublications(publications ?? [], projectId, videoIds);
  }, [projectId, publications, projectLoading, projectError, folder.videos, folder.drafts]);
  const visiblePopoverState = popover.state && (!projectId || displayedPublications?.some(publication => publication.id === popover.state?.inspectedPublicationId))
    ? popover.state : null;
  const previousProjectId = useRef(projectId);
  useEffect(() => {
    if (previousProjectId.current === projectId) return;
    previousProjectId.current = projectId;
    popover.close();
  }, [projectId, popover.close]);
  const clearProjectFilter = () => {
    popover.close();
    router.push(getUnfilteredCalendarHref(new URLSearchParams(searchParams.toString())), { scroll: false });
  };
  const now = useNow();
  const postNow = usePostNowAction({});
  const deletePost = useDeletePublicationAction();
  const returnDate =
    searchParams.get("currentDate") ?? format(new Date(), "yyyy-MM-dd");

  const showCard =
    !projectId && (status.kind === "no_shorts" || status.kind === "no_publications");

  const cleanTrustpilotReviewPromptParam = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams.toString());
    nextSearchParams.delete(TRUSTPILOT_REVIEW_PROMPT_QUERY_PARAM);
    const nextSearch = nextSearchParams.toString();
    router.replace(nextSearch ? `/calendar?${nextSearch}` : "/calendar", {
      scroll: false,
    });
  }, [router, searchParams]);

  useEffect(() => {
    if (handledTrustpilotPromptRef.current) return;
    if (searchParams.get(TRUSTPILOT_REVIEW_PROMPT_QUERY_PARAM) !== "1") return;

    handledTrustpilotPromptRef.current = true;
    cleanTrustpilotReviewPromptParam();

    if (!shouldShowTrustpilotReviewPrompt()) return;

    modal.open(
      <TrustpilotReviewDialog
        onClose={() => modal.close()}
        onReviewClick={() => storeTrustpilotReviewPromptState("completed")}
        onDismiss={() => storeTrustpilotReviewPromptState("dismissed")}
      />,
      { className: "max-w-md" },
    );
  }, [cleanTrustpilotReviewPromptParam, modal, searchParams]);

  // Document scrolling includes the gutters and toolbar. Clip horizontally
  // without creating a scroll container that would break the sticky header.
  // Phones keep the app header's 12px gutter: the grid is a bordered card.
  return (
    <CalendarAutoPublish publications={publications} onOpen={popover.close}>
      {({ wrapScheduleButton, onManageAutoPost }) => (
    <main className="max-w-[1500px] px-3 md:px-12 pb-2 mx-auto min-w-0 overflow-x-clip">
      {projectId ? <ProjectCalendarFilter projectId={projectId} onClear={clearProjectFilter} /> : null}
      {projectError ? (
        <div role="alert" className="flex flex-wrap items-center gap-2 pt-2 text-sm text-slate-500">
          <span>Couldn’t load this project’s posts.</span>
          <Button type="button" size="s" variant="tertiary" onClick={() => { void folder.refetch(); void publicationsQuery.refetch(); }}>Try again</Button>
        </div>
      ) : projectLoading ? (
        <div role="status" className="flex items-center gap-2 pt-2 text-sm text-slate-500"><Spinner className="size-4" /> Loading project posts…</div>
      ) : projectId && displayedPublications?.length === 0 ? (
        <p role="status" className="pt-2 text-sm text-slate-500">No posts from this project yet.</p>
      ) : null}
      <Calendar
        publications={displayedPublications}
        occupancyPublications={publications}
        isLoading={projectLoading || publicationsQuery.isLoading}
        wrapScheduleButton={wrapScheduleButton}
        // A day cell opens the same picker as "Schedule posts"; the day only
        // becomes the new posts' Starting date.
        onCreatePublicationOnDay={(date) => modal.open(
          <SelectVideoPublicationForm defaultDate={date} from="calendar" returnDate={format(date, "yyyy-MM-dd")} startDay={format(date, "yyyy-MM-dd")} />,
          { className: "h-full max-w-3xl", disabledPadding: true },
        )}
        onPublicationClick={popover.open}
        onInitialPublicationReady={popover.open}
        // The post the inspector is SHOWING wears the active surface. The
        // panel no longer walks to the destination tile when the user pages, so
        // this marker is the only thing that says which post is open — and it
        // is a plain read of popover state, not a side effect of re-opening
        // the panel.
        activePublicationId={visiblePopoverState?.inspectedPublicationId ?? null}
        // Reveal the destination tile while the inspector remains stationary.
        revealRequest={popover.reveal}
      />
      <CalendarPostPopover
        onManageAutoPost={onManageAutoPost}
        state={visiblePopoverState}
        publications={displayedPublications}
        now={now}
        onClose={popover.close}
        onInspect={popover.inspect}
        onEditPost={(publication, folderId) => {
          if (isDemoPublication(publication)) return showDemoPostNotice();
          if (!publication.projectId) return;
          popover.close();
          router.push(
            scheduleHrefForPublication({
              publication: { id: publication.id, projectId: publication.projectId },
              folderId,
              returnDate,
            }),
          );
        }}
        // A new tab, and the popover stays open behind it: the editor is a
        // different workspace, and the point of opening it separately is that
        // the month the user was reading is still here when they come back.
        onEditVideo={(video) => {
          window.open(
            editorHrefForVideo(video.id),
            "_blank",
            "noopener,noreferrer",
          );
        }}
        onOpenProject={(folderId) => {
          window.open(`/spaces/${folderId}`, "_blank", "noopener,noreferrer");
        }}
        onViewAnalytics={() => {
          popover.close();
          router.push("/analytics");
        }}
        onPostNow={(video, publication, account) => {
          if (isDemoPublication(publication)) return showDemoPostNotice();
          popover.close();
          postNow.requestPostNow({ id: video.id, name: video.name }, [
            { publication, account },
          ], () => router.push(scheduleHrefForPublication({
            publication: { id: publication.id, projectId: video.id },
            folderId: video.folderId,
            returnDate,
          })));
        }}
        onDelete={(publication, account) => {
          if (isDemoPublication(publication)) return showDemoPostNotice();
          popover.close();
          // The account can be gone (disconnected, or removed) — that post
          // is precisely the one worth deleting, and the confirm only needs
          // a name to say it out loud.
          deletePost.requestDelete(
            publication,
            account?.username || account?.name || "this account",
          );
        }}
        removal="immediate"
      />
      {postNow.dialogs}
      {deletePost.dialog}
      {showCard && (
        <div className="fixed inset-0 z-[35] flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-500/50" />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl mx-4 max-w-md w-full overflow-hidden">
            <video
              src="https://assets.klap.app/web-assets/showcase/schedule.mp4"
              autoPlay
              muted
              loop
              playsInline
              className="block w-full"
            />
            <div className="p-6">
              {status.kind === "no_shorts" ? (
                <ConfirmAction
                  title="Your schedule will appear here"
                  description="Generate shorts from your videos to start scheduling and tracking performance."
                  confirmLabel="Generate shorts"
                  onConfirm={() => router.push("/spaces")}
                />
              ) : (
                <ConfirmAction
                  title="Your schedule will appear here"
                  description="Schedule posts across TikTok, Instagram, Facebook, YouTube, and LinkedIn."
                  confirmLabel="Schedule posts"
                  onConfirm={openScheduleModal}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </main>
      )}
    </CalendarAutoPublish>
  );
};

export default CalendarContent;
